/**
 * Real-time event stream client.
 *
 * Establishes a Server-Sent Events (SSE) connection to the backend,
 * authenticated via the standard bearer token. Falls back gracefully when
 * the connection drops, automatically reconnecting with backoff.
 *
 * The native EventSource API cannot send custom headers, so we use the
 * native fetch API with a streaming reader to parse SSE frames manually.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "";
const TOKEN_KEY = "fs-token";

const getAuthToken = (): string | null => {
  if (typeof window === "undefined") return null;

  return (
    window.localStorage.getItem(TOKEN_KEY) ||
    window.sessionStorage.getItem(TOKEN_KEY)
  );
};

export type RealtimeEvent =
  | {
      type: "message:created";
      data: {
        conversationId: string;
        message: {
          _id: string;
          conversationId: string;
          senderId?: string;
          senderName: string;
          senderRole: "eleve" | "tuteur" | "system";
          body: string;
          attachments?: Array<{
            fileName: string;
            mimeType: string;
            size: number;
            url: string;
          }>;
          createdAt: string;
        };
      };
    }
  | {
      type: "notification:created";
      data: {
        notification: {
          _id: string;
          userId: string;
          title: string;
          body: string;
          type: string;
          event?: string;
          link?: string;
          read: boolean;
          createdAt: string;
        };
      };
    }
  | { type: "ready"; data: { ts: number } };

type Listener = (event: RealtimeEvent) => void;

const listeners = new Set<Listener>();
let connectionPromise: Promise<void> | null = null;
let abortController: AbortController | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;

const dispatch = (event: RealtimeEvent) => {
  for (const listener of listeners) {
    try {
      listener(event);
    } catch (error) {
      // Listener errors should never break the stream.
      console.error("realtime listener error", error);
    }
  }
};

const parseFrame = (frame: string): RealtimeEvent | null => {
  let eventName: string | null = null;
  const dataLines: string[] = [];

  for (const rawLine of frame.split("\n")) {
    if (!rawLine || rawLine.startsWith(":")) continue;
    const colonIndex = rawLine.indexOf(":");
    const field = colonIndex < 0 ? rawLine : rawLine.slice(0, colonIndex);
    const valueRaw = colonIndex < 0 ? "" : rawLine.slice(colonIndex + 1);
    const value = valueRaw.startsWith(" ") ? valueRaw.slice(1) : valueRaw;

    if (field === "event") eventName = value;
    else if (field === "data") dataLines.push(value);
  }
  if (!eventName || dataLines.length === 0) return null;
  try {
    const parsed = JSON.parse(dataLines.join("\n"));

    return {
      type: eventName as RealtimeEvent["type"],
      data: parsed,
    } as RealtimeEvent;
  } catch {
    return null;
  }
};

const startStream = async (): Promise<void> => {
  const token = getAuthToken();

  if (!token) return;

  abortController = new AbortController();
  let response: Response;

  try {
    response = await fetch(`${API_BASE}/api/realtime/stream`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/event-stream",
      },
      signal: abortController.signal,
      credentials: "same-origin",
    });
  } catch {
    scheduleReconnect();

    return;
  }

  if (!response.ok || !response.body) {
    scheduleReconnect();

    return;
  }

  reconnectAttempts = 0;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();

      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let separatorIndex: number;

      while ((separatorIndex = buffer.indexOf("\n\n")) >= 0) {
        const frame = buffer.slice(0, separatorIndex);

        buffer = buffer.slice(separatorIndex + 2);
        const parsed = parseFrame(frame);

        if (parsed) dispatch(parsed);
      }
    }
  } catch {
    // Reader error — fall through to reconnect.
  } finally {
    scheduleReconnect();
  }
};

const scheduleReconnect = () => {
  if (reconnectTimer) return;
  if (listeners.size === 0) {
    connectionPromise = null;

    return;
  }
  reconnectAttempts += 1;
  const delay = Math.min(30000, 1000 * 2 ** Math.min(reconnectAttempts - 1, 5));

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connectionPromise = null;
    if (listeners.size > 0) {
      connectionPromise = startStream();
    }
  }, delay);
};

const ensureConnection = () => {
  if (connectionPromise) return;
  if (typeof window === "undefined") return;
  if (!getAuthToken()) return;
  connectionPromise = startStream();
};

export const subscribeRealtime = (listener: Listener): (() => void) => {
  listeners.add(listener);
  ensureConnection();

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (abortController) {
        try {
          abortController.abort();
        } catch {
          // Already aborted.
        }
        abortController = null;
      }
      connectionPromise = null;
    }
  };
};
